import {
  DateValueEdit,
  EntryLinkSelect,
  HyperlinkDefaultValueSelect,
  JiraIssueSelect,
  NumberValuePopoverEdit,
  PageLinkAddDataSection,
  RichTextValuePopoverEdit,
  TagSelect,
  UserSelect
} from '@databases/fields/DefaultValuePickers';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import {
  DEFAULT_ENTRY_ID,
  DatabaseFieldType,
  type DatabaseField,
  type DatabaseValue,
  type RawDatabaseValue
} from '@databases/types/database';
import {
  type DatabaseChange,
  type ValueChange
} from '@databases/utils/database/changesets';
import { convertType } from '@databases/utils/database/field-types';
import { isNonEmptyJiraIssueValue } from '@databases/utils/database/field-types/jiraIssue';
import React, { type FunctionComponent, useCallback, useRef } from 'react';

import { type DefaultValueInputSlotProps } from './shared/DefaultValueInput';

type DefaultValuePickerProps = {
  addChangeToDraft: (change: DatabaseChange) => void;
  contentId: string;
  field: DatabaseField;
  hasValue: boolean;
  onChange: (value: DatabaseValue, change: ValueChange | null) => void;
  slotProps: DefaultValueInputSlotProps;
  value: DatabaseValue;
};

const DefaultValuePicker: FunctionComponent<DefaultValuePickerProps> = ({
  addChangeToDraft,
  contentId,
  field,
  hasValue,
  onChange,
  slotProps,
  value
}) => {
  const database = defaultStoreUtils.useSelector((state) =>
    selectDatabaseDraftByContentId(state, { contentId, includeDeleted: false })
  );
  const addDataSectionRef = useRef<HTMLDivElement | null>(null);

  const convertedValue = defaultStoreUtils.useEqualSelector((state) =>
    convertType({ field, value }, field.type, state)
  );

  const handleRawChange = useCallback(
    (rawValue: RawDatabaseValue, change: ValueChange | null) => {
      onChange(
        {
          type: field.type,
          value: rawValue
        } as DatabaseValue,
        change
      );
    },
    [field.type, onChange]
  );

  const sharedProps = {
    autoFocus: false,
    contentId,
    entryId: DEFAULT_ENTRY_ID,
    field,
    isDefaultValue: true,
    isRenderedInContextualFieldMenu: true,
    placeholder: slotProps.placeholder,
    ariaLabel: slotProps.ariaLabel,
    ...(slotProps.ariaDescribedBy
      ? { 'aria-describedby': slotProps.ariaDescribedBy }
      : {})
  };

  switch (field.type) {
    case DatabaseFieldType.SELECT: {
      const selectField = field as DatabaseField<DatabaseFieldType.SELECT>;
      const selectValue =
        convertedValue.value as RawDatabaseValue<DatabaseFieldType.SELECT>;
      const canCreate = Boolean(
        database?.operations.updateMetadata && !database.meta.isLocked
      );

      return (
        <div ref={addDataSectionRef}>
          <TagSelect
            options={selectField.settings.selectOptions}
            value={selectValue}
            isCreatable={canCreate}
            isMulti={selectField.settings.multi || selectValue.length > 1}
            onChange={handleRawChange}
            field={selectField}
            addChangeToDraft={addChangeToDraft}
            isRenderedInContextualFieldMenu
            addDataSectionRef={addDataSectionRef}
            placeholder={slotProps.placeholder}
          />
        </div>
      );
    }

    case DatabaseFieldType.USER: {
      const userField = field as DatabaseField<DatabaseFieldType.USER>;
      const userValue = convertedValue.value as RawDatabaseValue<DatabaseFieldType.USER>;
      const resolvedUsers = Array.isArray(userValue) ? userValue : [];

      return (
        <div ref={addDataSectionRef}>
          <UserSelect
            onChange={handleRawChange}
            contentId={contentId}
            field={userField}
            resolvedUsers={resolvedUsers}
            isMulti={userField.settings.multi || resolvedUsers.length > 1}
            addDataSectionRef={addDataSectionRef}
            scheduleMentionNotification={() => {}}
            isRenderedInContextualFieldMenu
          />
        </div>
      );
    }

    case DatabaseFieldType.ENTRY_LINK: {
      return (
        <div data-testid="entry-link-add-data-section" ref={addDataSectionRef}>
          <EntryLinkSelect
            value={convertedValue.value as RawDatabaseValue<DatabaseFieldType.ENTRY_LINK>}
            field={field as DatabaseField<DatabaseFieldType.ENTRY_LINK>}
            onChange={handleRawChange}
            isRenderedInContextualFieldMenu
            addDataSectionRef={addDataSectionRef}
          />
        </div>
      );
    }

    case DatabaseFieldType.JIRA_ISSUE: {
      const jiraField = field as DatabaseField<DatabaseFieldType.JIRA_ISSUE>;
      const jiraValue =
        convertedValue.value as RawDatabaseValue<DatabaseFieldType.JIRA_ISSUE>;
      const resolvedValues = jiraValue.map((jiraIssueValue) =>
        isNonEmptyJiraIssueValue(jiraIssueValue)
          ? { ...jiraIssueValue, serverId: jiraField.settings.jiraServerId }
          : jiraIssueValue
      );

      return (
        <div data-testid="jira-issue-add-data-section" ref={addDataSectionRef}>
          <JiraIssueSelect
            cloudId={jiraField.settings.cloudId}
            serverId={jiraField.settings.jiraServerId}
            onChange={handleRawChange}
            resolvedValues={resolvedValues}
            isMulti={jiraField.settings.multi || resolvedValues.length > 1}
            addDataSectionRef={addDataSectionRef}
            isRenderedInContextualFieldMenu
          />
        </div>
      );
    }

    case DatabaseFieldType.TEXT:
      return (
        <RichTextValuePopoverEdit
          {...sharedProps}
          field={field as DatabaseField<DatabaseFieldType.TEXT>}
          value={convertedValue.value as RawDatabaseValue<DatabaseFieldType.TEXT>}
          onChange={handleRawChange}
        />
      );

    case DatabaseFieldType.NUMBER:
      return (
        <NumberValuePopoverEdit
          {...sharedProps}
          field={field as DatabaseField<DatabaseFieldType.NUMBER>}
          value={
            hasValue
              ? (convertedValue.value as RawDatabaseValue<DatabaseFieldType.NUMBER>)
              : ''
          }
          onChange={handleRawChange}
        />
      );

    case DatabaseFieldType.DATE:
      return (
        <DateValueEdit
          {...sharedProps}
          field={field as DatabaseField<DatabaseFieldType.DATE>}
          value={convertedValue.value as RawDatabaseValue<DatabaseFieldType.DATE>}
          onChange={handleRawChange}
        />
      );

    case DatabaseFieldType.PAGE_LINK:
      return (
        <PageLinkAddDataSection
          {...sharedProps}
          field={field as DatabaseField<DatabaseFieldType.PAGE_LINK>}
          value={convertedValue.value as RawDatabaseValue<DatabaseFieldType.PAGE_LINK>}
          onChange={handleRawChange}
        />
      );

    case DatabaseFieldType.HYPERLINK:
      return (
        <HyperlinkDefaultValueSelect
          {...sharedProps}
          field={field as DatabaseField<DatabaseFieldType.HYPERLINK>}
          value={convertedValue.value as RawDatabaseValue<DatabaseFieldType.HYPERLINK>}
          onChange={handleRawChange}
        />
      );

    default:
      return null;
  }
};

export default DefaultValuePicker;
